import { useState, useRef, useEffect, useCallback } from "react";
import { copyToClipboard } from "../utils/helpers";

/**
 * State-driven transient "Copied!" feedback : the one implementation
 * of the copy-feedback idiom every tool page previously hand-rolled.
 *
 * The contract (unchanged from the CSRGenerator reference pattern the
 * pages copied) : `handleCopy(text, fieldKey)` puts `text` on the
 * clipboard through the shared copyToClipboard helper, flips
 * `copyStatus[fieldKey]` to the helper's success flag - a FAILED copy
 * never shows the confirmation - and clears the status after two
 * seconds. `resetCopyStatus` clears immediately (form resets, new
 * submissions).
 *
 * Why a hook instead of the ten per-page copies it replaced
 * (2026-08-07 download/copy consistency pass, operator-directed) :
 * the hand-rolled pattern never kept its setTimeout handle, so
 * copying field A and then field B one second later let A's
 * still-pending timer wipe B's "Copied!" after one second instead of
 * two. The hook owns ONE timer : each copy clears the previous timer
 * before arming its own, and the unmount cleanup clears whatever
 * timer is pending.
 *
 * The generation counter guards the async gap : the clipboard write
 * is awaited, so a resetCopyStatus (Start Over, a new submission) or
 * an unmount can land while a copy is in flight. Without the guard
 * the late resolution would undo the reset and show "Copied!" beside
 * a cleared form ; with it, any reset, newer copy, or unmount bumps
 * the generation and the stale resolution bails before touching
 * state. It also serializes two rapid copies : only the newest one's
 * resolution may write.
 */
export function useCopyFeedback() {
  const [copyStatus, setCopyStatus] = useState({});
  const timerRef = useRef(null);
  const generationRef = useRef(0);

  // Unmount cleanup : clear the pending timer and invalidate any
  // in-flight copy so neither touches state on a dead component.
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      generationRef.current += 1;
    },
    []
  );

  const handleCopy = useCallback(async (text, fieldKey) => {
    const generation = ++generationRef.current;
    const success = await copyToClipboard(text);
    // A reset, a newer copy, or an unmount happened while the
    // clipboard write was in flight : this resolution is stale.
    if (generationRef.current !== generation) return;
    clearTimeout(timerRef.current);
    setCopyStatus({ [fieldKey]: success });
    timerRef.current = setTimeout(() => setCopyStatus({}), 2000);
  }, []);

  const resetCopyStatus = useCallback(() => {
    generationRef.current += 1;
    clearTimeout(timerRef.current);
    setCopyStatus({});
  }, []);

  return { copyStatus, handleCopy, resetCopyStatus };
}
